import { Box, Chip } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import CustomAutocomplete from "../../../shared/AutoComplete";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomDatePicker from "../../../shared/CustomDate";
import ReusableTable from "../../../shared/ReusableTable/ReusableTable";
import { RootState } from "../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../store/tableSlice";
import basestyle from "../../CSS/Base.module.css";
import DeletePopUp from "../../PopUp/PopUp";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../StyledComponents/Container";
import CommonUtilities from "../Utils/CommonUtilities";
import { formatDateToMMDDYYYY } from "../Utils/DataFormat";
import { notifyUser } from "../Utils/notifyUser";
import { deleteInvoiceTransactionManualDetailsById, filterInvoiceTransactionManualEntries } from "../services/ManualEntries/manualEntriesServices";
import { getAllParticipants } from "../services/MonthlyCalculations/monthlyServices";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";

const typesOptions = CommonUtilities.invoiceTransactionTypes;

interface Column {
  id: string;
  label: string;
  minWidth: number | string;
  align?: "center" | "left" | "right";
}

interface Participant {
  participantId: number;
  participantName: string;
}


const formatAmount = (
  amount: number | null | undefined,
  locale: string = "en-US",
  currency: string = "USD"
): string => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount ?? 0);
};

const InvoicesPaymentsTransactions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const userInfo = useSelector((state: RootState) => state.auth);
  let role: any = userInfo.roles?.roleName;
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const [userRole] = useState<string>(contactInformation.participantAdminProfile);

  const retain = useSelector((state: RootState) => state.table.invoicesPaymentsTransactions);

  const [page, setPage] = useState(retain?.page || 1);
  const [openPopUp, setOpenPopUp] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(retain?.pageSize || 5);
  const [fromDate, setFromDate] = useState<string | null>(retain?.filters.fromDate || null);
  const [toDate, setToDate] = useState<string | null>(retain?.filters.toDate || null);
  const [selectedType, setSelectedType] = useState(retain?.filters.selectedType || "All");
  const [selectedParticipant, setSelectedParticipant] = useState<Participant>(
    retain?.filters.selectedParticipant || { participantId: 0, participantName: "All" }
  );
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupData, setPopupData] = useState<any>(null);
  const [count, setCount] = useState(0);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [participantArray, setParticipantArray] = useState<Participant[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const renderDashCentered = (value: any) => {
    if (!value || value === "-") {
      return <div style={{ textAlign: "center" }}>-</div>;
    }
    return value;
  };

  const columns: Column[] = [
    { id: "postingDate", label: translate("postingDate"), minWidth: "20%", align: "center" },
    { id: "sourceName", label: translate("savingsAccountName"), minWidth: "20%", align: "left" },
    { id: "description", label: translate("description"), minWidth: "35%", align: "left" },
    { id: "amount", label: translate("amount"), minWidth: "15%", align: "right" },
    ...(userRole === constants.ADMIN_VIEW
      ? [{ id: "participantName", label: translate("participantName"), minWidth: "23%", }]
      : []),
    { id: "type", label: translate("type"), minWidth: "10%", align: "left" },
  ];

  const handlePageChange = (newPage: number) => setPage(newPage + 1);
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };
  function handleDelete(row: Record<string, any>): void {
    setOpenPopUp(true);
    setDeleteUserId(row.id);
  }
  async function deleteById(dId: number, cId: number) {
    try {
      const deleteresponseList = await deleteInvoiceTransactionManualDetailsById(dId, cId);
      if (deleteresponseList?.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(
          deleteresponseList?.data?.message,
          constants.NOTIFY_STATUS.SUCCESS,
          translate("invoicesPaymentsTransactions")
        );
        handleClear();
      } else {

        notifyUser(
          deleteresponseList?.response?.data?.message || "Unexpected error occurred",
          constants.NOTIFY_STATUS.ERROR,
          translate("invoicesPaymentsTransactions")
        );
      }
    } catch (error: any) {
      notifyUser(error?.response?.data?.message || "Unexpected error occurred", constants.NOTIFY_STATUS.ERROR, translate("invoicesPaymentsTransactions"));
    }
  }
  function handleConfirmDelete() {
    deleteById(deleteUserId, userInfo?.clientId);
    handlePopUpClose();
  }
  const handlePopUpClose = () => {
    setOpenPopUp(false);
  };

  const handleSearch = () => {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      if (to < from) {
        toast.error(translate("toDateShouldBeGreaterThanFromDate"));
        return;
      }
    }
    setPage(1);
    getInvoicesPaymentsTransactionsListInfo(
      fromDate,
      toDate,
      selectedType,
      selectedParticipant,
      1,
      rowsPerPage
    );
  };

  const handleClear = () => {
    dispatch(resetTableState({ tableKey: "invoicesPaymentsTransactions" }));
    setFromDate(null);
    setToDate(null);
    setSelectedType("All");
    setSelectedParticipant({ participantId: 0, participantName: "All" });
    setPage(1);
    setRowsPerPage(5);

    getInvoicesPaymentsTransactionsListInfo(null, null, "All", { participantId: 0, participantName: "All" }, 1, 5);
  };

  async function getInvoicesPaymentsTransactionsListInfo(
    fromDate: string | null,
    toDate: string | null,
    selectedType: string,
    selectedParticipant: Participant,
    page: number,
    rowsPerPage: number
  ) {
    setIsLoading(true);

    try {
      const payload = {
        clientId: userInfo.clientId,
        participantId:
          userRole === constants.ADMIN_VIEW
            ? selectedParticipant?.participantId
            : userInfo?.defaultParticipantId,
        type: selectedType === "All" ? "" : selectedType,
        isActive: constants.IS_ACTIVE_YES,
        fromDate: fromDate ? new Date(fromDate).toISOString().replace("Z", "") : "",
        toDate: toDate ? new Date(toDate).toISOString().replace("Z", "") : "",
        page,
        size: rowsPerPage,
        sort: [{ field: "postingDate", direction: "DESC" }],
      };

      dispatch(
        setTableFilters({
          tableKey: "invoicesPaymentsTransactions",
          filters: {
            fromDate,
            toDate,
            selectedType,
            selectedParticipant,
          },
        })
      );
      dispatch(
        setPagination({
          tableKey: "invoicesPaymentsTransactions",
          page,
          pageSize: rowsPerPage,
        })
      );
      const response = await filterInvoiceTransactionManualEntries(payload);

      if (response.status === constants.STATUS_CODES.SUCCESS) {
        setCount(response.data.totalRecordCount);
        const updatedData = response.data.data?.map((item: any) => {
          const matchedParticipant = participantArray.find((p) => p.participantId === item.participantId);
          const participantName = matchedParticipant ? matchedParticipant.participantName : item.participantName || "-";

          return {
            ...item,
            postingDate: formatDateToMMDDYYYY(item.postingDate),
            amount: formatAmount(item.amount),
            description: item.comments || "-",
            participantName,
            type: item.type === "VI" ? "Vendor" : item.type === "DI" ? "Diocesan" : item.type,
          };
        });
        setFilteredData(updatedData || []);
      } else {
        setCount(0);
        setFilteredData([]);
        notifyUser(response.data.message, "error", translate("invoicesPaymentsTransactions"));
      }
    } catch (error) {
      console.error(translate("errorFetchingInvoicesPaymentsTransactionsData"), error);
    } finally {
      setIsLoading(false);
    }
  }

  async function getAllParticipantsList() {
    setIsLoading(true);
    try {
      const request = {
        participantName: "",
        isActive: constants.IS_ACTIVE_YES,
        clientId: userInfo.clientId,
      };
      const response = await getAllParticipants(request);

      if (response.status === constants.STATUS_CODES.SUCCESS) {
        const participants = response.data.data.map((p: any) => ({
          participantId: p.participantId,
          participantName: p.participantName,
        }));
        setParticipantArray([{ participantId: 0, participantName: "All" }, ...participants]);
      } else {
        setParticipantArray([]);
      }
    } catch (error) {
      console.error("Error fetching Participants List:", error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getAllParticipantsList();
  }, []);

  useEffect(() => {
    if (participantArray.length > 0) {
      getInvoicesPaymentsTransactionsListInfo(
        fromDate,
        toDate,
        selectedType,
        selectedParticipant,
        page,
        rowsPerPage
      );
    }
  }, [participantArray, page, rowsPerPage]);

  const handleRowClick = (list: any) => {
    setPopupData(list);
    setPopupOpen(true);
  };

  const invoicesPaymentsTransactionsTableList = filteredData?.map((list: any) => ({
    ...list,
    postingDate: renderDashCentered(list?.postingDate),
    participantName: renderDashCentered(list?.participantName),
    payload:
      list?.payload && list.payload.length > 40 ? (
        <span
          onClick={() => handleRowClick(list)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            whiteSpace: "nowrap",
          }}
        >
          {list.payload.slice(0, 52) + "..."}
          <Chip
            label="More"
            size="small"
            variant="outlined"
            color="primary"
            onClick={(e: any) => {
              e.stopPropagation();
              handleRowClick(list);
            }}
          />
        </span>
      ) : (
        list?.payload
      ),
    type: renderDashCentered(list?.type),
    userId: renderDashCentered(list?.createdBy),
  }));

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>{translate("invoicesPaymentsTransactions")}</SectionTitle>
          <Box
            sx={{
              px: 1.5,
              mt: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                flex: "1 1 auto",
              }}
            >
              <CustomDatePicker
                sx={{ width: { xs: "100%", sm: 180 } }}
                label={translate("fromDate")}
                value={fromDate ? dayjs(fromDate) : null}
                onChange={(newValue) =>
                  setFromDate(newValue ? newValue.format("MM-DD-YYYY") : null)
                }
              />
              <CustomDatePicker
                sx={{ width: { xs: "100%", sm: 180 } }}
                label={translate("toDate")}
                value={toDate ? dayjs(toDate) : null}
                onChange={(newValue) =>
                  setToDate(newValue ? newValue.format("MM-DD-YYYY") : null)
                }
              />
              {userRole === constants.ADMIN_VIEW && (
                <CustomAutocomplete
                  size="small"
                  sx={{ width: { xs: "100%", sm: 250 } }}
                  options={participantArray}
                  value={selectedParticipant}
                  onChange={(event, newValue) => {
                    if (!newValue) return;
                    setSelectedParticipant(newValue);
                    setPage(1);
                    getInvoicesPaymentsTransactionsListInfo(
                      fromDate,
                      toDate,
                      selectedType,
                      newValue,
                      1,
                      rowsPerPage
                    );
                  }}
                  getOptionLabel={(option) => option.participantName}
                  isOptionEqualToValue={(option, value) => option.participantId === value?.participantId}
                  label={translate("participant")}
                  placeholder={translate("participant")}
                />
              )}
              <CustomAutocomplete
                label={translate("type")}
                sx={{ width: { xs: "100%", sm: 250 } }}
                options={typesOptions.filter((t) => t.value)}
                value={typesOptions.find((t) => t.value === selectedType) || null}
                onChange={(_, newValue) => {
                  if (!newValue) return;
                  const value = newValue.value;
                  setSelectedType(value);
                  setPage(1);
                  getInvoicesPaymentsTransactionsListInfo(
                    fromDate,
                    toDate,
                    value,
                    selectedParticipant,
                    1,
                    rowsPerPage
                  );
                }}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                placeholder={constants.SELECT_PLACEHOLDER}
              />
              <ContainedButton onClick={handleSearch}>{translate("search")}</ContainedButton>
              <OutlinedButton onClick={handleClear}>{translate("clear")}</OutlinedButton>
            </Box>
            {![...(ClientDetails?.name === constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_READ_ONLY_USER, constants.ROLES_PROJECT_MANAGER, constants.ROLES_BUDGET_ANALYST] : [])].includes(role) && (<Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                minWidth: { xs: "100%", sm: "auto" },
              }}
            >
              <ContainedButton onClick={() => navigate("/invoicesPayments")}>
                {translate("addTransaction")}
              </ContainedButton>
            </Box>
            )}
          </Box>
          <Box sx={{ width: "100%", px: 1.5, mt: 3 }}>
            <ReusableTable
              columns={columns}
              rows={invoicesPaymentsTransactionsTableList}
              count={count}
              page={page - 1}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              loading={isLoading}
              onDelete={(ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && [constants.ROLES_READ_ONLY_USER, constants.ROLES_PROJECT_MANAGER, constants.ROLES_BUDGET_ANALYST].includes(role)) ? undefined : handleDelete}
            />
            {(ClientDetails?.name === constants.CLIENT_NAME_DC_GARY && ![constants.ROLES_PROJECT_MANAGER].includes(role))&& <div className={basestyle.buttoncontainer}>
              <ContainedButton onClick={() => navigate(userRole === constants.ADMIN_VIEW ? "/rcboSummary" : "/summary")}>
                {translate("back")}
              </ContainedButton>
            </div>}
            <DeletePopUp
              open={openPopUp}
              popupTitle={translate("confirmDelete")}
              handleClose={handlePopUpClose}
              saveChanges={() => handleConfirmDelete()}
              message={translate("doYouWanttoInactivate?")}
            />
          </Box>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};
export default InvoicesPaymentsTransactions;
